// Resize an image file to max 1200px on its longest side and re-encode as
// JPEG 0.8 before uploading. Keeps phone-camera photos (often 8-12 MB) under
// the server's 3 MB limit and makes every tank photo load fast.
export function resizeImageFile(file, { maxDim = 1200, quality = 0.82 } = {}) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode image'))),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file didn't open as an image"));
    };
    img.src = url;
  });
}

// Try the canvas resize FIRST for every format — including HEIC.
// Safari (every iPhone) decodes HEIC natively, so the common case
// uploads a ~200 KB JPEG instead of a multi-MB raw HEIC that the
// server must crunch through heic-convert (memory-heavy enough to
// 502 a small container — user-reported). Raw bytes are the
// fallback only when THIS browser truly can't decode the file
// (e.g. HEIC on Firefox/Windows); the server's normaliseImage
// remains the final authority on decodability.
// The largest image any upload route takes (the timeline and journal photos).
const RAW_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;

export async function prepareImageUpload(file, opts) {
  try {
    return { body: await resizeImageFile(file, opts), mime: 'image/jpeg' };
  } catch {
    // Sending it anyway only spent the keeper's data on a 413: a 36 MB
    // iPhone ProRAW photo, which Safari cannot open to shrink, did exactly
    // that on 2026-09-28.
    if (file.size > RAW_UPLOAD_MAX_BYTES) {
      const mb = Math.ceil(file.size / (1024 * 1024));
      const raw = /x-adobe-dng|\.dng$|\braw\b/i.test(`${file.type} ${file.name}`);
      throw new Error(
        raw
          ? `That is a RAW photo (${mb} MB), which can't be sent here. Choose a normal photo, or switch RAW off in the Camera app for tank shots.`
          : `That photo is too large to send (${mb} MB). Choose a smaller photo or a screenshot of it.`
      );
    }
    const looksHeic =
      /image\/(heic|heif)/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '');
    return { body: file, mime: file.type || (looksHeic ? 'image/heic' : 'image/jpeg') };
  }
}

export async function uploadTankPhoto(tankId, file) {
  const { body, mime } = await prepareImageUpload(file);
  const res = await fetch(`/api/tanks/${tankId}/photo`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': mime },
    body,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err.error || 'Upload failed');
  }
  return res.json();
}

// Upload a Near You photo (store / club / event) — canvas-first like
// tank photos. Returns { id, url }; callers stash url in the submission's
// image_url so the card renders it.
export async function uploadNearImage(file) {
  const { body, mime } = await prepareImageUpload(file);
  const res = await fetch('/api/near/images', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': mime },
    body,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err.error || 'Upload failed');
  }
  return res.json();
}

// API client — all calls include the session cookie. Throws on non-2xx so
// callers can do a single try/catch per flow.
export async function api(path, { method = 'GET', body, signal } = {}) {
  let res;
  try {
    res = await fetch(path, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    // No answer at all, whatever words this browser uses for it.
    if (e && typeof e === 'object' && e.name !== 'AbortError') e.network = true;
    throw e;
  }
  const ct = res.headers.get('content-type') || '';
  const payload = ct.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);
  if (!res.ok) {
    const err = new Error(payload?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

// fetch() throws "Failed to fetch" (Chrome), "Load failed" (Safari),
// "NetworkError ..." (Firefox) or an older WebKit's "The network connection
// was lost." when no answer came back at all, which reads like our own
// error. api() marks those (network); our own code's TypeError (a null reply
// read after the server acted) is not a lost signal.
export function networkWords(e) {
  return e?.network === true ||
    /^(failed to fetch|load failed|networkerror)/i.test(e?.message || '')
    ? 'Could not reach TankSensei. Check your signal and try again.'
    : null;
}
